import { useState, useEffect } from "react";
import { Product, Facets, CategoryText } from "../types";
import { createCacheId } from "../utils/cacheUtils";
import { eventBus, EVENTS } from "../utils/eventBus";

export interface ProductData {
  products?: Product[];
  count?: number;
  flags?: { [key: string]: string[] };
  compareList?: number[];
  compareFull?: boolean;
  wishlist?: Record<string, number>;
}

// Cache configuration
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes in milliseconds

// Cache helper functions
const cacheHelper = {
  get(key: string): any {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      const { data, timestamp } = JSON.parse(item);

      // Check if cache is expired
      if (Date.now() - timestamp > CACHE_TTL) {
        localStorage.removeItem(key);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Cache get error:", error);
      return null;
    }
  },

  set(key: string, data: any): void {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.error("Cache set error:", error);
    }
  },
};

export const fetchProductsData = async (
  groupId: string,
  queryString: string
) => {
  if (!groupId) {
    return null;
  }

  const cacheIdHash = createCacheId(
    [groupId],
    queryString || { page: "1" },
    "products"
  );
  // Check all caches first
  const cachedProductData = cacheHelper.get(cacheIdHash);

  if (cachedProductData) {
    return cachedProductData;
  }

  try {
    const productsResponse = await fetch(
      `/catalog/group/${groupId}/products${
        queryString ? `?${queryString}` : ""
      }`
    );

    const productsData = await productsResponse.json();

    // Cache the fresh data
    cacheHelper.set(cacheIdHash, productsData);

    return productsData;
  } catch (error) {
    console.error("Error fetching catalog data:", error);
    throw error;
  }
};

export const fetchFacetsData = async (groupId: string, queryString: string) => {
  const cacheFacetDataKey = createCacheId([groupId], queryString, "facets");
  // Check all caches first
  const cachedFacetData = cacheHelper.get(cacheFacetDataKey);

  if (cachedFacetData) {
    eventBus.emit(EVENTS.FACETS_LOADED, cachedFacetData);
    return cachedFacetData;
  }

  try {
    const [facetsResponse] = await Promise.all([
      fetch(
        `/catalog/group/${groupId}/facets${
          queryString ? `?${queryString}` : ""
        }`
      ),
    ]);

    const facetsData = await facetsResponse.json();

    // Cache the fresh data
    cacheHelper.set(cacheFacetDataKey, facetsData);

    // Emit event for freshly fetched data
    eventBus.emit(EVENTS.FACETS_LOADED, facetsData);

    return facetsData;
  } catch (error) {
    console.error("Error fetching catalog data:", error);
    throw error;
  }
};

export const useAppData = (groupId: string, queryString: string) => {
  const [productData, setProductData] = useState<ProductData | null>(null);
  const [facetData, setFacetData] = useState<Facets | null>(null);
  const [categoryText, setCategoryText] = useState<CategoryText | null>(null);
  const [viewBlockHtml, setViewBlockHtml] = useState<string>("");
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterLoading, setFilterLoading] = useState<boolean>(false);
  const [viewBlockLoading, setViewBlockLoading] = useState<boolean>(false);
  const [viewBlockError, setViewBlockError] = useState<string | null>(null);

  // Effect for static data (category text, banner) - only runs once when groupId changes
  useEffect(() => {
    if (!groupId) {
      setLoading(false);
      return;
    }

    // Only set main loading to true on initial load (when we don't have any data yet)
    if (!productData && !facetData) {
      setLoading(true);
    } else {
      // If we already have data, this is a filter update - use filterLoading instead
      setFilterLoading(true);
    }
    setError(null);

    // Create cache keys for all API calls
    const cacheProductDataKey = createCacheId(
      [groupId],
      queryString,
      "products"
    );
    const cacheFacetDataKey = createCacheId([groupId], queryString, "facets");
    const cacheCategoryTextKey = createCacheId([groupId], {}, "category-text");
    const cacheViewBlockKey = createCacheId([groupId], {}, "view-block");

    // Check all caches first
    const cachedProductData = cacheHelper.get(cacheProductDataKey);
    const cachedFacetData = cacheHelper.get(cacheFacetDataKey);
    const cachedCategoryText = cacheHelper.get(cacheCategoryTextKey);
    const cachedViewBlock = cacheHelper.get(cacheViewBlockKey);

    // Set cached data immediately if available
    if (cachedProductData) {
      setProductData(cachedProductData);
    }
    if (cachedFacetData) {
      setFacetData(cachedFacetData);
    }
    if (cachedCategoryText) {
      setCategoryText(cachedCategoryText);
    }
    if (cachedViewBlock) {
      setViewBlockHtml(cachedViewBlock);
    }

    // Prepare all API calls
    const apiCalls = [];

    // 1. Products API call
    if (!cachedProductData || cachedFacetData?.data?.products?.length === 0) {
      apiCalls.push(
        fetchProductsData(groupId, queryString).then((productsData) => {
          setProductData(productsData);
          return productsData;
        })
      );
    }

    // if (productData?.products) {
    //   apiCalls.push(
    //     fetchProductBadges(productData).then((productBadges) => {
    //       setProductBadges(productBadges);
    //       return productBadges;
    //     })
    //   );
    // }

    if (
      !cachedFacetData ||
      Object.keys(cachedFacetData?.data || {}).length === 0
    ) {
      apiCalls.push(
        fetchFacetsData(groupId, queryString).then((facetsData) => {
          setFacetData(facetsData);
          return facetsData;
        })
      );
    }

    // 3. Category Text API call
    if (!cachedCategoryText) {
      apiCalls.push(
        fetch(`/group_page/${groupId}/text`)
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Failed to fetch category text: ${response.status}`
              );
            }
            return response.json();
          })
          .then((categoryTextData) => {
            setCategoryText(categoryTextData);
            cacheHelper.set(cacheCategoryTextKey, categoryTextData);
            return categoryTextData;
          })
          .catch((err) => {
            console.error("Error fetching category text:", err);
            // Don't set main error for category text, it's not critical
            return null;
          })
      );
    }

    // 4. View Block API call
    if (!cachedViewBlock) {
      setViewBlockLoading(true);
      setViewBlockError(null);

      apiCalls.push(
        fetch(`/group_page_banner/${groupId}`)
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Failed to fetch block HTML: ${response.status} ${response.statusText}`
              );
            }
            return response.text();
          })
          .then((html) => {
            setViewBlockHtml(html);
            setViewBlockError(null);
            cacheHelper.set(cacheViewBlockKey, html);
            return html;
          })
          .catch((err) => {
            console.error("Error fetching Drupal view block:", err);
            setViewBlockError(err.message || "Failed to load block content.");
            setViewBlockHtml(""); // Clear HTML on error
            // Don't set main error for view block, it's not critical
            return null;
          })
          .finally(() => {
            setViewBlockLoading(false);
          })
      );
    }

    // Execute all API calls in parallel
    if (apiCalls.length > 0) {
      Promise.allSettled(apiCalls)
        .then((results) => {
          // Check if any critical API calls failed
          const criticalFailures = results.filter(
            (result, index) => result.status === "rejected" && index < 2 // Only products and facets are critical
          );

          if (criticalFailures.length > 0) {
            console.error("Critical API calls failed:", criticalFailures);
          }
        })
        .finally(() => {
          setLoading(false);
          setFilterLoading(false);
        });
    } else {
      // All data was cached, no API calls needed
      setLoading(false);
      setFilterLoading(false);
    }
  }, [groupId, queryString]);

  return {
    productData,
    facetData,
    categoryText,
    viewBlockHtml,
    loading,
    filterLoading,
    error,
    viewBlockLoading,
    viewBlockError,
  };
};
